import { useState, useEffect } from "react";
import { useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { MessageSquareHeart, X, Loader2, Star, Sparkles } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "../lib/axiosClient";
import { useAuth } from "../context/AuthContext";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from "./ui/dialog";

const BANNER_DISMISS_KEY = "cgi_dev_banner_dismissed_v1";

// Thin top ribbon shown on every page until dismissed. Persists via localStorage.
export const DevBanner = () => {
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    setVisible(localStorage.getItem(BANNER_DISMISS_KEY) !== "1");
  }, []);

  const dismiss = () => {
    localStorage.setItem(BANNER_DISMISS_KEY, "1");
    setVisible(false);
  };

  if (!visible) return null;
  return (
    <div
      className="w-full bg-[#003566] text-white text-sm"
      data-testid="dev-banner"
    >
      <div className="max-w-5xl mx-auto px-4 sm:px-6 py-2 flex items-center gap-3">
        <Sparkles className="w-4 h-4 flex-shrink-0 text-[#E8C766]" strokeWidth={2} />
        <p className="flex-1 leading-snug">
          <strong>This app is under development.</strong>{" "}
          <span className="opacity-90">Your feedback shapes it — please tell us what would help.</span>
        </p>
        <button
          onClick={dismiss}
          data-testid="dev-banner-dismiss"
          aria-label="Dismiss notice"
          className="p-1 rounded-full hover:bg-white/10 transition-colors flex-shrink-0"
        >
          <X className="w-4 h-4" />
        </button>
      </div>
    </div>
  );
};

// Star selector (1-5)
const StarPicker = ({ value, onChange }) => (
  <div className="flex items-center gap-1" data-testid="feedback-rating">
    {[1, 2, 3, 4, 5].map((n) => (
      <button
        key={n}
        type="button"
        onClick={() => onChange(value === n ? null : n)}
        data-testid={`feedback-rating-${n}`}
        aria-label={`Rate ${n} star${n === 1 ? "" : "s"}`}
        className="p-1 hover:scale-110 transition-transform"
      >
        <Star
          className="w-6 h-6"
          strokeWidth={1.8}
          fill={value && value >= n ? "#E8C766" : "none"}
          stroke={value && value >= n ? "#B89A3E" : "#8A8782"}
        />
      </button>
    ))}
  </div>
);

// Reusable dialog that any page can open.
export const FeedbackDialog = ({ open, onOpenChange }) => {
  const { user } = useAuth();
  const location = useLocation();
  const [rating, setRating] = useState(null);
  const [message, setMessage] = useState("");
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [sent, setSent] = useState(false);

  useEffect(() => {
    if (open) {
      setSent(false);
      // Prefill from auth if available
      setName(user?.name || "");
      setEmail(user?.email || "");
    }
  }, [open, user]);

  const submit = async (e) => {
    e.preventDefault();
    if (!message.trim()) {
      toast.error("Please share a little of what you're thinking.");
      return;
    }
    setSubmitting(true);
    try {
      await api.post("/feedback", {
        message: message.trim(),
        rating,
        name: name.trim() || undefined,
        email: email.trim() || undefined,
        page: location.pathname,
      });
      setSent(true);
      setMessage("");
      setRating(null);
      toast.success("Thank you — your feedback is in.");
    } catch (err) {
      toast.error(formatApiError(err, "Couldn't send feedback — please try again."));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="max-w-lg bg-[#FDFBF7] border-[#E8E2D9]"
        data-testid="feedback-dialog"
      >
        <DialogHeader>
          <DialogTitle className="font-serif-display text-3xl text-[#2D2A26] font-semibold leading-tight">
            Please…PLEASE! We want your feedback.
          </DialogTitle>
          <DialogDescription className="text-[#5C5A56]">
            Help us improve. What worked, what didn’t, what would you love to see?
            Anything — a sentence or a paragraph — helps us shape this for you.
          </DialogDescription>
        </DialogHeader>

        {sent ? (
          <div className="py-6 text-center" data-testid="feedback-sent">
            <div className="w-12 h-12 mx-auto rounded-full bg-[#EBF2EA] flex items-center justify-center mb-3">
              <MessageSquareHeart className="w-5 h-5 text-[#8A9A86]" strokeWidth={2} />
            </div>
            <p className="text-[#2D2A26] font-medium">Received — thank you.</p>
            <p className="text-sm text-[#5C5A56] mt-1">We read every note.</p>
            <button
              onClick={() => onOpenChange(false)}
              className="mt-6 inline-flex items-center gap-2 px-6 py-2.5 rounded-full border-2 border-[#E8E2D9] hover:border-[#D1C7B8] text-sm font-medium bg-white text-[#2D2A26] transition-all"
            >
              Close
            </button>
          </div>
        ) : (
          <form onSubmit={submit} className="space-y-4">
            <div>
              <label className="block text-xs font-semibold uppercase tracking-[0.18em] text-[#8A8782] mb-2">
                Overall, how was it? (optional)
              </label>
              <StarPicker value={rating} onChange={setRating} />
            </div>

            <div>
              <label className="block text-xs font-semibold uppercase tracking-[0.18em] text-[#8A8782] mb-2">
                What would help us improve?
              </label>
              <textarea
                value={message}
                onChange={(e) => setMessage(e.target.value)}
                required
                rows={5}
                maxLength={4000}
                placeholder="One question, one wish, one thing that got in the way…"
                data-testid="feedback-message-input"
                className="w-full px-4 py-3 rounded-2xl border-2 border-[#E8E2D9] bg-white text-[#2D2A26] placeholder-[#8A8782] focus:outline-none focus:border-[#C86B5E] transition-colors resize-none"
              />
            </div>

            {!user && (
              <div className="grid sm:grid-cols-2 gap-3">
                <input
                  type="text"
                  placeholder="Your name (optional)"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  maxLength={80}
                  data-testid="feedback-name-input"
                  className="px-4 py-2.5 rounded-full border-2 border-[#E8E2D9] bg-white text-[#2D2A26] text-sm focus:outline-none focus:border-[#C86B5E] transition-colors"
                />
                <input
                  type="email"
                  placeholder="Email (only if you'd like a reply)"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  data-testid="feedback-email-input"
                  className="px-4 py-2.5 rounded-full border-2 border-[#E8E2D9] bg-white text-[#2D2A26] text-sm focus:outline-none focus:border-[#C86B5E] transition-colors"
                />
              </div>
            )}

            <div className="flex items-center justify-end gap-2 pt-2">
              <button
                type="button"
                onClick={() => onOpenChange(false)}
                className="px-5 py-2.5 rounded-full border-2 border-[#E8E2D9] hover:border-[#D1C7B8] text-sm font-medium bg-white text-[#2D2A26] transition-all"
              >
                Not now
              </button>
              <button
                type="submit"
                disabled={submitting}
                data-testid="feedback-submit-button"
                className="inline-flex items-center justify-center gap-2 px-6 py-2.5 rounded-full bg-[#C86B5E] text-white text-sm font-medium hover:bg-[#B35B4E] disabled:opacity-60 transition-all shadow-sm"
              >
                {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : <MessageSquareHeart className="w-4 h-4" />}
                Send feedback
              </button>
            </div>
          </form>
        )}
      </DialogContent>
    </Dialog>
  );
};

// Floating pill visible on every page (bottom-right).
export const FeedbackFab = () => {
  const [open, setOpen] = useState(false);
  return (
    <>
      <AnimatePresence>
        <motion.button
          key="fab"
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.35 }}
          onClick={() => setOpen(true)}
          data-testid="feedback-fab"
          className="fixed z-40 bottom-5 right-5 sm:bottom-6 sm:right-6 group inline-flex items-center gap-2 pl-3 pr-5 py-3 rounded-full bg-white border-2 border-[#C86B5E] text-[#C86B5E] hover:bg-[#C86B5E] hover:text-white shadow-lg hover:shadow-xl transition-all active:scale-95 max-w-[calc(100vw-2.5rem)]"
        >
          <span className="w-8 h-8 rounded-full bg-[#F8EBE9] group-hover:bg-white/20 flex items-center justify-center transition-colors">
            <MessageSquareHeart className="w-4 h-4" strokeWidth={2} />
          </span>
          <span className="text-sm font-semibold whitespace-nowrap hidden sm:inline">Please…PLEASE! Give feedback</span>
          <span className="text-sm font-semibold whitespace-nowrap sm:hidden">Feedback</span>
        </motion.button>
      </AnimatePresence>
      <FeedbackDialog open={open} onOpenChange={setOpen} />
    </>
  );
};

// Larger inline card intended for the bottom of Results / Couple results pages.
export const FeedbackCallout = () => {
  const [open, setOpen] = useState(false);
  return (
    <>
      <div
        className="mt-12 rounded-3xl border-2 border-dashed border-[#C86B5E]/60 bg-[#FDF5EE] p-6 sm:p-8 text-center"
        data-testid="feedback-callout"
      >
        <MessageSquareHeart className="w-8 h-8 mx-auto text-[#C86B5E]" strokeWidth={1.8} />
        <h3 className="mt-3 font-serif-display text-2xl sm:text-3xl text-[#2D2A26] font-semibold leading-tight">
          Please…PLEASE! We want your feedback.
        </h3>
        <p className="mt-2 text-sm sm:text-base text-[#5C5A56] max-w-xl mx-auto">
          This app is still under development. What would make this more useful for you?
          What confused you? What made you smile? A sentence goes a long way.
        </p>
        <button
          onClick={() => setOpen(true)}
          data-testid="feedback-callout-button"
          className="mt-5 inline-flex items-center gap-2 bg-[#C86B5E] text-white rounded-full px-7 py-3 font-medium text-sm hover:bg-[#B35B4E] transition-all shadow-sm hover:shadow-md active:scale-[0.98]"
        >
          <MessageSquareHeart className="w-4 h-4" />
          Help us improve
        </button>
      </div>
      <FeedbackDialog open={open} onOpenChange={setOpen} />
    </>
  );
};
